// Ported from prototypes/shared/engine.js (the reviewed prototype).
import { addWatchDetail } from './synth-watch.js';
/* Prototype engine: seeded synthetic runners + report metrics.
 * Runs in the browser (window.RunEngine) and in Node (module.exports) so the
 * numbers shown in prototypes are the same ones the tests check.
 * Everything here is FAKE demo data, labelled as such in the UI. */

  // ---------- utilities ----------
  function mulberry32(seed) {
    return function () {
      seed |= 0; seed = (seed + 0x6d2b79f5) | 0;
      let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }
  const DAY = 86400000;
  const dayKey = (d) => d.toISOString().slice(0, 10);
  const fromKey = (k) => new Date(k + 'T00:00:00Z');
  const addDays = (d, n) => new Date(d.getTime() + n * DAY);
  const daysBetween = (a, b) => Math.round((b - a) / DAY);
  // Monday-based week start
  const weekStart = (d) => addDays(d, -((d.getUTCDay() + 6) % 7));
  const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
  const median = (xs) => {
    if (!xs.length) return null;
    const s = [...xs].sort((a, b) => a - b), m = s.length >> 1;
    return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
  };

  function fmtPace(secPerKm) {
    if (!isFinite(secPerKm)) return '–';
    const m = Math.floor(secPerKm / 60), s = Math.round(secPerKm % 60);
    return s === 60 ? `${m + 1}:00` : `${m}:${String(s).padStart(2, '0')}`;
  }
  function fmtDuration(sec) {
    sec = Math.round(sec);
    const h = Math.floor(sec / 3600), m = Math.floor((sec % 3600) / 60), s = sec % 60;
    return h ? `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}` : `${m}:${String(s).padStart(2, '0')}`;
  }
  function fmtHours(sec) { return `${Math.floor(sec / 3600).toLocaleString()}h ${String(Math.round((sec % 3600) / 60)).padStart(2, '0')}m`; }

  // ---------- personas ----------
  // A persona is a training story; the generator turns it into runs.
  const PERSONAS = {
    maya: {
      id: 'maya', name: 'Maya', blurb: 'Consistent intermediate · watch with heart rate · two races',
      seed: 11, start: '2026-01-02', maxHr: 186, hasHr: 1, indoorShare: 0.06,
      easyPace: 352, // sec/km at start of year
      fitnessGain: 0.045, // pace improves ~4.5% across the year
      template: [ // weekday (0=Mon) -> session
        { dow: 1, kind: 'easy', km: 7, hour: 6 },
        { dow: 3, kind: 'workout', km: 8, hour: 6 },
        { dow: 5, kind: 'easy', km: 6, hour: 8 },
        { dow: 6, kind: 'long', km: 13, hour: 8 },
      ],
      skip: 0.12,
      blocks: [ // volume multipliers by date
        { from: '2026-01-01', mult: 0.85 }, { from: '2026-02-15', mult: 1.0 },
        { from: '2026-04-20', mult: 0.8 }, { from: '2026-05-10', mult: 1.0 },
        { from: '2026-08-15', mult: 1.15 }, { from: '2026-10-05', mult: 0.85 },
      ],
      breaks: [{ from: '2026-07-06', to: '2026-07-27', why: 'holiday' }],
      spikes: ['2026-03-01', '2026-08-30', '2026-09-20'],
      races: [
        { date: '2026-03-22', distM: 10000, name: 'Riverside 10K', effort: 1.0 },
        { date: '2026-04-19', distM: 21097.5, name: 'Spring Half Marathon', effort: 1.0 },
      ],
      shoes: [{ name: 'Daily trainer A', from: '2026-01-01', priorKm: 410 }, { name: 'Daily trainer B', from: '2026-06-01', priorKm: 0 }],
      routes: [
        { name: 'Canal loop', km: 7.1, elevPerKm: 3 }, { name: 'Park laps', km: 6.0, elevPerKm: 5 },
        { name: 'Hill circuit', km: 8.4, elevPerKm: 18 }, { name: 'River out-and-back', km: 12.0, elevPerKm: 4 },
      ],
      sources: { strava: 0.97, garmin: 0.96 },
      // synthetic watch detail: a run-walker (about 10% of time walking)
      watch: { seed: 111, walkShare: 0.1, runSegS: 260, vo2: [43, 46] },
    },
    sam: {
      id: 'sam', name: 'Sam', blurb: 'New runner since May · about 20 runs · phone GPS, no heart rate',
      seed: 23, start: '2026-05-04', maxHr: null, hasHr: 0, indoorShare: 0,
      easyPace: 425, fitnessGain: 0.07,
      template: [{ dow: 1, kind: 'easy', km: 3.2, hour: 18 }, { dow: 5, kind: 'easy', km: 4, hour: 9 }],
      skip: 0.42,
      blocks: [{ from: '2026-05-01', mult: 0.8 }, { from: '2026-07-01', mult: 1.1 }, { from: '2026-09-01', mult: 1.3 }],
      breaks: [{ from: '2026-08-10', to: '2026-08-31', why: 'unknown' }],
      spikes: [], races: [],
      shoes: [], routes: [{ name: 'Block loop', km: 3.0, elevPerKm: 6 }, { name: 'Park', km: 4.2, elevPerKm: 4 }],
      sources: { strava: 1, garmin: 0 },
    },
    jo: {
      id: 'jo', name: 'Jo', blurb: 'Treadmill only · no GPS, no heart rate · very regular',
      seed: 37, start: '2026-01-05', maxHr: null, hasHr: 0, indoorShare: 1,
      easyPace: 380, fitnessGain: 0.02,
      template: [{ dow: 0, kind: 'easy', km: 5, hour: 19 }, { dow: 2, kind: 'easy', km: 5, hour: 19 }, { dow: 4, kind: 'easy', km: 6, hour: 18 }],
      skip: 0.08, blocks: [{ from: '2026-01-01', mult: 1 }], breaks: [], spikes: [], races: [],
      shoes: [{ name: 'Gym shoes', from: '2026-01-01', priorKm: 120 }], routes: [],
      sources: { strava: 1, garmin: 0 },
    },
    alex: {
      id: 'alex', name: 'Alex', blurb: 'Marathon build · 5–6 runs a week · heart rate · three races',
      seed: 41, start: '2026-01-01', maxHr: 191, hasHr: 1, indoorShare: 0.03,
      easyPace: 318, fitnessGain: 0.04,
      template: [
        { dow: 0, kind: 'easy', km: 8, hour: 6 }, { dow: 1, kind: 'workout', km: 12, hour: 6 },
        { dow: 2, kind: 'easy', km: 10, hour: 6 }, { dow: 3, kind: 'workout', km: 11, hour: 6 },
        { dow: 5, kind: 'easy', km: 8, hour: 7 }, { dow: 6, kind: 'long', km: 22, hour: 7 },
      ],
      skip: 0.1,
      blocks: [
        { from: '2026-01-01', mult: 0.8 }, { from: '2026-03-01', mult: 0.95 }, { from: '2026-05-01', mult: 0.85 },
        { from: '2026-06-15', mult: 1.05 }, { from: '2026-08-01', mult: 1.2 }, { from: '2026-09-21', mult: 0.7 },
      ],
      breaks: [{ from: '2026-05-25', to: '2026-06-12', why: 'calf niggle' }],
      spikes: ['2026-06-21', '2026-08-09', '2026-08-30'],
      races: [
        { date: '2026-02-15', distM: 10000, name: 'Winter 10K', effort: 1.0 },
        { date: '2026-04-12', distM: 21097.5, name: 'City Half Marathon', effort: 1.0 },
        { date: '2026-10-04', distM: 42195, name: 'Autumn Marathon', effort: 1.0 },
      ],
      shoes: [{ name: 'Trainer', from: '2026-01-01', priorKm: 300 }, { name: 'Racer', from: '2026-01-01', priorKm: 80, raceOnly: true }],
      routes: [
        { name: 'Ridge loop', km: 10.2, elevPerKm: 14 }, { name: 'Track + warmup', km: 11.0, elevPerKm: 1 },
        { name: 'Towpath', km: 16.0, elevPerKm: 2 }, { name: 'Long valley', km: 24.0, elevPerKm: 8 },
      ],
      sources: { strava: 0.95, garmin: 0.99 },
      // synthetic watch detail: a continuous runner
      watch: { seed: 141, walkShare: 0, runSegS: 0, vo2: [51, 54] },
    },
  };
  PERSONAS.duo = Object.assign({}, PERSONAS.maya, {
    id: 'duo', name: 'Maya (Strava + Garmin)', blurb: 'Same runner as Maya, with both exports uploaded',
  });

  // ---------- generator ----------
  function blockMult(p, date) {
    let m = 1;
    for (const b of p.blocks) if (date >= fromKey(b.from)) m = b.mult;
    return m;
  }
  function inBreak(p, date) {
    return p.breaks.some((b) => date >= fromKey(b.from) && date <= fromKey(b.to));
  }

  function generate(personaId, endKey = '2026-10-14') {
    const p = PERSONAS[personaId];
    const rnd = mulberry32(p.seed);
    const noise = (s) => (rnd() * 2 - 1) * s;
    const start = fromKey(p.start), end = fromKey(endKey), yearStart = fromKey('2026-01-01');
    const yearLen = daysBetween(yearStart, end);
    const runs = [];
    const raceByDay = Object.fromEntries(p.races.map((r) => [r.date, r]));
    const spikeDays = new Set(p.spikes);
    let id = 1;

    for (let d = start; d <= end; d = addDays(d, 1)) {
      const key = dayKey(d);
      const dow = (d.getUTCDay() + 6) % 7;
      const progress = daysBetween(yearStart, d) / yearLen;
      const fitness = 1 - p.fitnessGain * progress; // multiplier on pace (lower = faster)
      const race = raceByDay[key];
      let session = null;
      if (race) session = { kind: 'race', km: race.distM / 1000, hour: 8 };
      else if (!inBreak(p, d)) {
        const t = p.template.find((s) => s.dow === dow);
        // taper week before a race: skip workouts
        if (t && rnd() > p.skip) session = Object.assign({}, t);
      }
      if (!session) continue;

      // volume
      let km;
      if (session.kind === 'race') km = session.km;
      else {
        km = session.km * blockMult(p, d) * (1 + noise(0.08));
        if (spikeDays.has(key)) km = session.km * blockMult(p, d) * 1.45;
      }
      const indoor = rnd() < p.indoorShare || (p.indoorShare === 1);
      const route = !indoor && p.routes.length ? p.routes[Math.floor(rnd() * p.routes.length)] : null;
      if (route && session.kind === 'easy' && rnd() < 0.6) km = route.km * (1 + noise(0.02));
      km = Math.round(km * 100) / 100;

      // pace
      const base = p.easyPace * fitness;
      let pace;
      if (session.kind === 'race') pace = base * (race.distM <= 10000 ? 0.82 : race.distM <= 21100 ? 0.87 : 0.955);
      else if (session.kind === 'workout') pace = base * (0.9 + noise(0.03));
      else if (session.kind === 'long') pace = base * (1.03 + noise(0.03));
      else pace = base * (1 + noise(0.04));
      if (indoor) pace *= 0.99;
      const movingS = Math.round(km * pace);
      const stopF = 1.02 + rnd() * 0.05;
      const elapsedS = Math.round(movingS * (indoor ? 1.0 : session.kind === 'race' ? 1.004 : stopF));
      const elevGainM = indoor ? 0 : Math.round(km * (route ? route.elevPerKm : 6) * (0.8 + rnd() * 0.4));

      // heart rate + zones (5-zone % of max HR model, for demo only)
      let avgHr = null, maxHr = null, zones = null;
      if (p.hasHr && rnd() > 0.04) {
        const effort = session.kind === 'race' ? 0.89 : session.kind === 'workout' ? 0.84 : session.kind === 'long' ? 0.77 : 0.74;
        const drift = 1 - p.fitnessGain * 0.6 * progress; // same pace costs less HR later in the year
        avgHr = Math.round(p.maxHr * clamp(effort * (session.kind === 'race' ? 1 : drift) + noise(0.02), 0.6, 0.96));
        maxHr = Math.min(p.maxHr, Math.round(avgHr * (1.06 + rnd() * 0.04)));
        zones = zoneSplit(session.kind, rnd);
      }

      // local start time
      const hour = clamp(Math.round(session.hour + noise(1.2)), 4, 22);
      const minute = Math.floor(rnd() * 60);

      // gear
      let gear = null;
      if (p.shoes.length) {
        const eligible = p.shoes.filter((s) => d >= fromKey(s.from) && (!s.raceOnly || session.kind === 'race'));
        const racer = eligible.find((s) => s.raceOnly);
        gear = (session.kind === 'race' && racer ? racer : eligible.filter((s) => !s.raceOnly).pop() || eligible[0] || {}).name || null;
      }

      // which sources hold this run (for the merge demo)
      // (only the 'duo' persona is shown as two uploads; others are single-source)
      // draw both every time so 'duo' and 'maya' produce identical runs
      const rs = rnd(), rg = rnd();
      let inStrava = rs < p.sources.strava;
      let inGarmin = personaId === 'duo' && rg < p.sources.garmin;
      if (personaId !== 'duo') inStrava = true;
      if (!inStrava && !inGarmin) inStrava = true;

      runs.push({
        id: id++, date: key, hour, minute,
        type: indoor ? 'Virtual Run' : (route && route.elevPerKm > 15 ? 'Trail Run' : 'Run'),
        kind: session.kind, isRace: session.kind === 'race', raceName: race ? race.name : null,
        indoor, hasGps: !indoor, route: route ? route.name : null,
        distM: Math.round(km * 1000), movingS, elapsedS, elevGainM, avgHr, maxHr, zones, gear,
        inStrava, inGarmin,
      });
    }
    if (p.watch) addWatchDetail(runs, p.watch);
    return { persona: p, runs, end: endKey };
  }

  function zoneSplit(kind, rnd) {
    const n = (a) => a.map((v) => Math.max(0, v + (rnd() * 2 - 1) * 4));
    const raw = kind === 'race' ? n([2, 6, 22, 45, 25]) : kind === 'workout' ? n([10, 28, 22, 30, 10])
      : kind === 'long' ? n([12, 55, 26, 6, 1]) : n([20, 60, 16, 3, 1]);
    const s = raw.reduce((a, b) => a + b, 0);
    return raw.map((v) => v / s);
  }

  // ---------- metrics ----------
  const EVIDENCE = {
    data: { key: 'data', label: 'Your data' },
    obs: { key: 'obs', label: 'Research: observational' },
    limited: { key: 'limited', label: 'Research: limited' },
    convention: { key: 'convention', label: 'Convention' },
  };
  // Official race time is the finish (elapsed) time, never moving time.
  const raceTime = (r) => r.elapsedS;
  // Distances the Vickers & Vertosick regression models were built on.
  const NOMINAL = [{ m: 5000, label: '5K' }, { m: 10000, label: '10K' }, { m: 21097.5, label: 'Half marathon' }];
  const nominalOf = (distM) => NOMINAL.find((n) => Math.abs(distM - n.m) / n.m <= 0.03) || null;

  function report(gen, opts = {}) {
    const startKey = opts.start || '2026-01-01', endKey = opts.end || gen.end;
    // History-dependent metrics (breaks, jumps, baseline, predictions, shoe lifetime) use everything
    // up to the period's end; display metrics use only the period. Fresh copies so annotations never leak.
    const history = gen.runs.filter((r) => r.date <= endKey).map((r) => Object.assign({}, r));
    // A period never starts before the runner's first-ever run.
    const firstEver = gen.runs.length ? gen.runs[0].date : startKey;
    const effStart = firstEver > startKey ? firstEver : startKey;
    const startedLater = effStart > startKey;
    const runs = history.filter((r) => r.date >= effStart);
    const start = fromKey(effStart), end = fromKey(endKey);
    const periodDays = daysBetween(start, end) + 1;

    const byDay = new Map();
    for (const r of runs) { if (!byDay.has(r.date)) byDay.set(r.date, []); byDay.get(r.date).push(r); }
    const totals = {
      runs: runs.length,
      distM: runs.reduce((a, r) => a + r.distM, 0),
      movingS: runs.reduce((a, r) => a + r.movingS, 0),
      elevGainM: runs.reduce((a, r) => a + r.elevGainM, 0),
      activeDays: byDay.size,
      indoorRuns: runs.filter((r) => r.indoor).length,
      withHr: runs.filter((r) => r.avgHr).length,
    };

    // ----- gaps: before the first run of the period, between runs, and since the last run -----
    const days = [...byDay.keys()].sort();
    const gaps = [];
    if (days.length) {
      const d0 = fromKey(days[0]);
      if (!startedLater && days[0] > effStart) {
        const prev = history.filter((r) => r.date < effStart).pop();
        gaps.push({ kind: 'lead', first: prev ? dayKey(addDays(fromKey(prev.date), 1)) : effStart, last: dayKey(addDays(d0, -1)), days: daysBetween(prev ? fromKey(prev.date) : addDays(start, -1), d0) - 1, startedBefore: !!prev });
      }
      for (let i = 1; i < days.length; i++) {
        const g = daysBetween(fromKey(days[i - 1]), fromKey(days[i])) - 1;
        if (g > 0) gaps.push({ kind: 'mid', first: dayKey(addDays(fromKey(days[i - 1]), 1)), last: dayKey(addDays(fromKey(days[i]), -1)), days: g });
      }
      const lastDay = fromKey(days[days.length - 1]);
      const trail = daysBetween(lastDay, end);
      if (trail > 0) gaps.push({ kind: 'trail', first: dayKey(addDays(lastDay, 1)), last: endKey, days: trail });
    }
    const longestGap = gaps.reduce((a, g) => (g.days > a.days ? g : a), { days: 0 });
    const breaks = gaps.filter((g) => g.days >= 14);
    const currentBreak = breaks.find((g) => g.kind === 'trail') || null;
    const midGaps = gaps.filter((g) => g.kind === 'mid' && g.days >= 8 && g.days < 14).length;

    // day streak
    let longestStreak = { len: 0 }, cur = null;
    for (const k of days) {
      const d = fromKey(k);
      if (cur && daysBetween(fromKey(cur.end), d) === 1) { cur.end = k; cur.len++; } else cur = { start: k, end: k, len: 1 };
      if (cur.len > longestStreak.len) longestStreak = Object.assign({}, cur);
    }

    // breaks over the full history (used to pause jump detection after time off)
    const histDays = [...new Set(history.map((r) => r.date))].sort();
    const histBreaks = [];
    for (let i = 1; i < histDays.length; i++) {
      const g = daysBetween(fromKey(histDays[i - 1]), fromKey(histDays[i])) - 1;
      if (g >= 14) histBreaks.push({ first: dayKey(addDays(fromKey(histDays[i - 1]), 1)), last: dayKey(addDays(fromKey(histDays[i]), -1)), back: histDays[i], days: g });
    }

    // ----- weeks (Mon–Sun) -----
    const weeks = [];
    for (let w = weekStart(start); w <= end; w = addDays(w, 7)) {
      const rs = runs.filter((r) => { const d = fromKey(r.date); return d >= w && d < addDays(w, 7); });
      weeks.push({ week: dayKey(w), km: rs.reduce((a, r) => a + r.distM, 0) / 1000, runs: rs.length, partial: w < start || addDays(w, 6) > end });
    }
    weeks.forEach((w, i) => { const win = weeks.slice(Math.max(0, i - 3), i + 1); w.avg4 = win.reduce((a, x) => a + x.km, 0) / win.length; });
    let weekStreak = { len: 0 }, ws = null;
    for (const w of weeks) {
      if (w.runs) { ws = ws ? { start: ws.start, end: w.week, len: ws.len + 1, runs: ws.runs + w.runs } : { start: w.week, end: w.week, len: 1, runs: w.runs }; if (ws.len > weekStreak.len) weekStreak = Object.assign({}, ws); } else ws = null;
    }
    const months = [];
    for (let ms = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), 1)); ms <= end; ms = new Date(Date.UTC(ms.getUTCFullYear(), ms.getUTCMonth() + 1, 1))) {
      const mEnd = new Date(Date.UTC(ms.getUTCFullYear(), ms.getUTCMonth() + 1, 0));
      const key = dayKey(ms).slice(0, 7);
      const rs = runs.filter((r) => r.date.slice(0, 7) === key);
      months.push({ key, month: ms.getUTCMonth(), year: ms.getUTCFullYear(), km: rs.reduce((a, r) => a + r.distM, 0) / 1000, runs: rs.length, partial: ms < start || mEnd > end });
    }

    // rhythm
    const grid = Array.from({ length: 7 }, () => Array(24).fill(0));
    for (const r of runs) grid[(fromKey(r.date).getUTCDay() + 6) % 7][r.hour]++;

    // ----- single-run jumps (Frandsen et al. 2025 exposure definition), over full history -----
    const firstDate = history.length ? fromKey(history[0].date) : null;
    for (const r of history) {
      const d = fromKey(r.date);
      if (daysBetween(firstDate, d) < 30) { r.spike = { status: 'insufficient' }; continue; }
      if (histBreaks.some((b) => d >= fromKey(b.back) && daysBetween(fromKey(b.back), d) < 30)) { r.spike = { status: 'after-break' }; continue; }
      const prior = history.filter((x) => { const xd = fromKey(x.date); return xd < d && daysBetween(xd, d) <= 30; });
      const L = prior.reduce((a, x) => Math.max(a, x.distM), 0);
      if (!L) { r.spike = { status: 'insufficient' }; continue; }
      const ratio = r.distM / L;
      const band = ratio > 2 ? '>100%' : ratio > 1.3 ? '30–100%' : ratio > 1.1 ? '10–30%' : null;
      r.spike = { status: band ? 'spike' : 'ok', ratio, priorLongest: L, band };
    }
    const spikesTraining = runs.filter((r) => r.spike.status === 'spike' && !r.isRace);
    const spikesRace = runs.filter((r) => r.spike.status === 'spike' && r.isRace);
    const training = runs.filter((r) => !r.isRace);
    const spikeCheck = { checked: training.filter((r) => r.spike.status === 'ok' || r.spike.status === 'spike').length, notChecked: training.filter((r) => r.spike.status === 'insufficient' || r.spike.status === 'after-break').length };

    // ----- best efforts -----
    // Races: official time at a recognised distance. Otherwise (prototype): the run's average pace
    // over the distance, labelled as such. The real app uses second-by-second streams.
    const bestEfforts = [{ label: '5K', m: 5000 }, { label: '10K', m: 10000 }, { label: 'Half', m: 21097.5 }, { label: 'Marathon', m: 42195 }].map((e) => {
      const cands = runs.filter((r) => r.distM >= e.m * 0.97);
      if (!cands.length) return { label: e.label, value: null };
      const best = cands.reduce((a, r) => {
        const isRaceAt = r.isRace && Math.abs(r.distM - e.m) / e.m <= 0.03;
        // fastest stretch from the watch's distance channel when we have it; otherwise the run's average pace
        const stream = r.best && r.best[e.m];
        const t = isRaceAt ? raceTime(r) : stream ? stream : r.distM >= e.m ? (r.movingS / r.distM) * e.m : Infinity;
        return !a || t < a.t ? { t, r, isRaceAt, stream: !isRaceAt && !!stream } : a;
      }, null);
      return isFinite(best.t) ? { label: e.label, value: best.t, run: best.r, fromRace: best.isRaceAt, fromStream: best.stream } : { label: e.label, value: null };
    });

    // ----- intensity: coverage measured as share of moving time -----
    const hrRuns = runs.filter((r) => r.zones);
    const hrTime = hrRuns.reduce((a, r) => a + r.movingS, 0);
    const hrCoverage = totals.movingS ? hrTime / totals.movingS : 0;
    let zoneTotals = null;
    if (hrRuns.length >= 10 && hrCoverage >= 0.7) {
      zoneTotals = [0, 0, 0, 0, 0];
      for (const r of hrRuns) r.zones.forEach((z, i) => (zoneTotals[i] += z * r.movingS));
      zoneTotals = zoneTotals.map((z) => z / hrTime);
    }

    // ----- aerobic efficiency: metres per heartbeat on easy, flat, outdoor runs -----
    const effRuns = runs.filter((r) => r.avgHr && r.kind === 'easy' && !r.indoor && r.elevGainM / (r.distM / 1000) < 10);
    const mpb = (r) => (r.distM / (r.movingS / 60)) / r.avgHr;
    const effByMonth = [];
    for (const mo of months) {
      const rs = effRuns.filter((r) => r.date.slice(0, 7) === mo.key);
      if (rs.length >= 4) effByMonth.push({ key: mo.key, month: mo.month, year: mo.year, value: median(rs.map(mpb)), n: rs.length });
    }
    const efficiency = { months: effByMonth, n: effRuns.length, meetsMinimum: false, change: null };
    if (effRuns.length >= 12) {
      const f0 = fromKey(effRuns[0].date), f1 = fromKey(effRuns[effRuns.length - 1].date);
      const early = effRuns.filter((r) => daysBetween(f0, fromKey(r.date)) < 42), late = effRuns.filter((r) => daysBetween(fromKey(r.date), f1) < 42);
      if (daysBetween(f0, f1) >= 56 && early.length >= 4 && late.length >= 4) {
        efficiency.meetsMinimum = true;
        efficiency.early = { n: early.length, value: median(early.map(mpb)), from: early[0].date, to: early[early.length - 1].date };
        efficiency.late = { n: late.length, value: median(late.map(mpb)), from: late[0].date, to: late[late.length - 1].date };
        efficiency.change = efficiency.late.value / efficiency.early.value - 1;
      }
    }

    // ----- shoes: lifetime from full history; "in use" = used in the last 60 days -----
    const gear = gen.persona.shoes.map((s) => {
      const all = history.filter((r) => r.gear === s.name), inWin = runs.filter((r) => r.gear === s.name);
      const lastUsed = all.length ? all[all.length - 1].date : null;
      return { name: s.name, windowKm: inWin.reduce((a, r) => a + r.distM, 0) / 1000, lifetimeKm: s.priorKm + all.reduce((a, r) => a + r.distM, 0) / 1000, lastUsed, inUse: !!lastUsed && daysBetween(fromKey(lastUsed), end) <= 60 };
    }).filter((g) => g.windowKm > 0);

    // ----- baseline: the 8 complete weeks before the current week, from full history -----
    const thisWeek = weekStart(end);
    const firstWeekEver = weekStart(fromKey(firstEver));
    const bw = [];
    for (let i = 8; i >= 1; i--) {
      const w = addDays(thisWeek, -7 * i);
      if (w < firstWeekEver) continue;
      const rs = history.filter((r) => { const d = fromKey(r.date); return d >= w && d < addDays(w, 7); });
      bw.push({ week: dayKey(w), km: rs.reduce((a, r) => a + r.distM, 0) / 1000, runs: rs.length });
    }
    const in30 = (r) => daysBetween(fromKey(r.date), end) <= 30;
    const baseline = {
      asOf: endKey, weeksCounted: bw.length,
      kmPerWeek: bw.reduce((a, w) => a + w.km, 0) / (bw.length || 1),
      runsPerWeek: bw.reduce((a, w) => a + w.runs, 0) / (bw.length || 1),
      weeksWithRun: bw.filter((w) => w.runs).length,
      longest30: history.filter(in30).reduce((a, r) => Math.max(a, r.distM), 0),
      longestTraining30: history.filter((r) => in30(r) && !r.isRace).reduce((a, r) => Math.max(a, r.distM), 0),
      longestEver: history.reduce((a, r) => Math.max(a, r.distM), 0),
      runsEver: history.length,
    };
    const lastLongRace = history.filter((r) => r.isRace && r.distM >= 21000 && daysBetween(fromKey(r.date), end) <= 28).pop();
    if (lastLongRace) {
      const before = history.filter((r) => !r.isRace && r.date < lastLongRace.date && daysBetween(fromKey(r.date), fromKey(lastLongRace.date)) <= 42);
      baseline.recentRace = { run: lastLongRace, daysAgo: daysBetween(fromKey(lastLongRace.date), end), lastTrainingLong: before.reduce((a, r) => (!a || r.distM > a.distM ? r : a), null) };
    }
    const lastHistBreak = histBreaks[histBreaks.length - 1];
    if (lastHistBreak && daysBetween(fromKey(lastHistBreak.back), end) < 30) baseline.postBreak = lastHistBreak;
    if (currentBreak) baseline.currentBreak = currentBreak;

    // ----- indoor-only summary -----
    let indoor = null;
    if (runs.length >= 10 && totals.indoorRuns / runs.length >= 0.7) {
      const dow = [0, 0, 0, 0, 0, 0, 0]; runs.forEach((r) => dow[(fromKey(r.date).getUTCDay() + 6) % 7]++);
      const top = dow.map((c, i) => ({ c, i })).filter((d) => d.c > 0).sort((a, b) => b.c - a.c).slice(0, 3);
      const p = runs.map((r) => r.movingS / (r.distM / 1000)).sort((a, b) => a - b);
      const q = (x) => p[Math.min(p.length - 1, Math.round(x * (p.length - 1)))];
      indoor = { topDays: top.map((d) => d.i), topShare: top.reduce((a, d) => a + d.c, 0) / runs.length, medianMin: median(runs.map((r) => r.movingS / 60)), paceP25: q(0.25), paceP75: q(0.75) };
    }

    const predictions = predict(history, end, baseline.kmPerWeek / 1.609344);

    const rpt = {
      persona: gen.persona, start: effStart, windowStart: startKey, startedLater, isYear: startKey === '2026-01-01' && !opts.end, end: endKey, periodDays,
      history, runs, totals, gaps, longestGap, midGaps, breaks, currentBreak, longestStreak, weeks, weekStreak, months, grid,
      spikesTraining, spikesRace, spikeCheck, bestEfforts, hrCoverage, zoneTotals, efficiency, gear, baseline, indoor, predictions,
    };
    rpt.minimums = {
      story: totals.runs >= 5,
      rhythm: totals.runs >= 20,
      zones: !!zoneTotals,
      spikes: totals.runs >= 10 && !!firstDate && daysBetween(firstDate, end) >= 45 && spikeCheck.checked >= 5,
      efficiency: efficiency.meetsMinimum,
      predictions: predictions.inputs.length > 0 && !predictions.hidden,
      places: runs.some((r) => r.hasGps && r.route),
    };
    rpt.suggestions = suggestions(rpt);
    return rpt;
  }

  // ---------- race prediction (Vickers & Vertosick 2016; docs/science-notes.md) ----------
  // Residual centiles (predicted − observed, seconds) from their Table 4 (validation n = 156).
  const VV_RESID = {
    m1: { p10: -(18 * 60 + 25), p25: -(9 * 60 + 55), p75: 6 * 60 + 28, p90: 11 * 60 + 49 },
    m2: { p10: -(16 * 60 + 47), p25: -(7 * 60 + 31), p75: 7 * 60 + 20, p90: 15 * 60 + 1 },
  };
  function milesPerWeekBefore(runs, date) {
    const d = fromKey(date);
    const rs = runs.filter((r) => { const x = fromKey(r.date); return x < d && daysBetween(x, d) <= 56; });
    return rs.reduce((a, r) => a + r.distM, 0) / 1609.344 / 8;
  }
  // most recent race at each recognised distance within 12 months, sorted by distance
  function raceInputs(races, end) {
    const byNom = {};
    for (const r of [...races].sort((a, b) => (a.date < b.date ? 1 : -1))) {
      const n = nominalOf(r.distM);
      if (!n || daysBetween(fromKey(r.date), end) > 365) continue;
      if (!byNom[n.m]) byNom[n.m] = Object.assign({ nominal: n, t: raceTime(r) }, { run: r });
    }
    return Object.values(byNom).sort((a, b) => a.nominal.m - b.nominal.m);
  }
  function vvMarathon(inputs, miles) {
    const r2 = inputs[inputs.length - 1], r1 = inputs.length >= 2 ? inputs[inputs.length - 2] : null;
    let t, model, resid, from;
    if (r1 && r2.nominal.m / r1.nominal.m >= 1.5) {
      const k21 = Math.log(r2.t / r1.t) / Math.log(r2.nominal.m / r1.nominal.m);
      const kM = 1.4510756 - 0.23797948 * k21 - 0.01410023 * (miles / 10);
      t = r2.t * Math.pow(42195 / r2.nominal.m, kM); model = 'Vickers–Vertosick model 2'; resid = VV_RESID.m2; from = [r1, r2];
    } else {
      const vR = 42195 / (r2.t * Math.pow(42195 / r2.nominal.m, 1.07));
      const v = 0.16018617 + 0.83076202 * vR + 0.06423826 * (miles / 10);
      t = 42195 / v; model = 'Vickers–Vertosick model 1'; resid = VV_RESID.m1; from = [r2];
    }
    return { label: 'Marathon', model, t, from, miles, band50: [t - resid.p75, t - resid.p25], band80: [t - resid.p90, t - resid.p10] };
  }
  function predict(history, end, milesNow) {
    const out = { inputs: [], items: [], check: null, miles: milesNow, hidden: null };
    const marathons = history.filter((r) => r.isRace && r.distM >= 42000);
    const shorter = history.filter((r) => r.isRace && r.distM < 42000);
    if (marathons.length) {
      const m = marathons[marathons.length - 1];
      const before = raceInputs(shorter.filter((r) => r.date < m.date), fromKey(m.date));
      if (before.length) {
        const p = vvMarathon(before, milesPerWeekBefore(history, m.date));
        out.check = Object.assign(p, { actual: raceTime(m), race: m, within80: raceTime(m) >= p.band80[0] && raceTime(m) <= p.band80[1] });
      }
    }
    const inputs = raceInputs(shorter, end);
    out.inputs = inputs;
    if (!inputs.length) return out;
    if (milesNow < 5) { out.hidden = 'low-mileage'; return out; }
    const longest = inputs[inputs.length - 1];
    for (const target of NOMINAL) {
      if (target.m <= longest.nominal.m) continue;
      out.items.push({ label: target.label, model: 'Riegel (k = 1.07)', t: longest.t * Math.pow(target.m / longest.nominal.m, 1.07), from: [longest] });
    }
    if (!marathons.some((m) => daysBetween(fromKey(m.date), end) <= 365)) out.items.push(vvMarathon(inputs, milesNow));
    return out;
  }

  // every tagged race in the full history, newest last
  function raceHistory(gen) {
    return gen.runs.filter((r) => r.isRace).map((r) => ({ run: r, t: raceTime(r), nominal: r.raceNominal || (nominalOf(r.distM) || {}).m || null }));
  }

  // ---------- keep / change / try ----------
  // Rules only; each names the chapter that triggered it. Bodies take a distance formatter so units follow the toggle.
  const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const monthName = (m) => MONTHS[m];
  const shortDate = (k) => { const d = typeof k === 'string' ? fromKey(k) : k; return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()].slice(0, 3)}`; };
  const plural = (n, one, many) => `${typeof n === 'number' ? n.toLocaleString() : n} ${n === 1 ? one : many || one + 's'}`;

  function suggestions(r) {
    const out = [];
    const weeksWithRun = r.weeks.filter((w) => w.runs > 0).length;
    const periodWord = r.startedLater ? 'since your first run' : 'in this period';
    if (r.weeks.length >= 4 && weeksWithRun / r.weeks.length >= 0.85) out.push({ type: 'keep', title: 'Steady weeks', body: () => `You ran in ${weeksWithRun} of ${r.weeks.length} weeks ${periodWord}.`, chart: 'volume', evidence: EVIDENCE.data });
    if (r.baseline.currentBreak) out.push({ type: 'change', title: 'Getting going again', body: () => `You haven't run since ${shortDate(r.baseline.currentBreak.first)} (${plural(r.baseline.currentBreak.days, 'day')}). Building back up gradually after time off is common advice rather than a tested rule.`, chart: 'calendar', evidence: EVIDENCE.convention });
    else if (r.breaks.length) out.push({ type: 'change', title: 'Plan around breaks', body: () => `${r.breaks.length === 1 ? 'One break' : r.breaks.length + ' breaks'} of 2+ weeks (${r.breaks.map((b) => b.days + ' days').join(', ')}). Building back up gradually after time off is common advice rather than a tested rule.`, chart: 'calendar', evidence: EVIDENCE.convention });
    const n = r.spikesTraining.length;
    if (n >= 2) out.push({ type: 'change', title: 'Long runs that stepped up sharply', body: () => `${n} training runs were more than 10% longer than your longest of the previous 30 days. In one large study, runs like that were associated with more overuse injuries.`, chart: 'longruns', evidence: EVIDENCE.obs });
    else if (r.minimums.spikes && r.spikeCheck.checked >= 20) out.push({ type: 'keep', title: 'Gradual long runs', body: () => `${n === 0 ? 'None' : 'One'} of the ${r.spikeCheck.checked} training runs we could check went more than 10% past your longest of the previous 30 days.`, chart: 'longruns', evidence: EVIDENCE.obs });
    const worn = r.gear.filter((g) => g.inUse && g.lifetimeKm >= 650);
    if (worn.length) out.push({ type: 'try', title: 'Check your shoes', body: (fd) => `${worn.map((g) => `${g.name}: ${fd(g.lifetimeKm * 1000, 0)} lifetime`).join('; ')}. Many runners replace shoes somewhere around 500–800 km (310–500 mi). That's a rule of thumb, not a tested threshold, so go by how they feel too.`, chart: 'gear', evidence: EVIDENCE.convention });
    const e = r.efficiency;
    if (e.meetsMinimum && e.change > 0.03) out.push({ type: 'keep', title: 'More distance per heartbeat', body: () => `On regular flat runs you now cover ${Math.round(e.change * 100)}% more ground per heartbeat than in your first six weeks of them. Heat, terrain and sensor noise also move this, so treat it as a hint.`, chart: 'efficiency', evidence: EVIDENCE.data });
    // newer runners: best stretch from their own history, and the next distance milestone
    if (r.baseline.runsEver < 40) {
      const st = r.weekStreak;
      if (st.len >= 2) out.push({ type: 'keep', title: 'Your best stretch', body: () => `${st.len} weeks in a row with a run (${shortDate([st.start, r.start].sort()[1])} – ${shortDate([dayKey(addDays(fromKey(st.end), 6)), r.end].sort()[0])}), averaging ${(st.runs / st.len).toFixed(1)} runs a week. You've run in ${r.baseline.weeksWithRun} of the last ${r.baseline.weeksCounted} weeks.`, chart: 'volume', evidence: EVIDENCE.data });
      const L = r.baseline.longestEver;
      const next = [5000, 10000].find((m) => L < m * 0.995);
      if (next && L > 0) out.push({ type: 'try', title: `Your first ${next / 1000}K`, body: (fd) => `Your longest run so far is ${fd(L, 1)}. ${next / 1000}K is ${fd(next - L, 1)} further. In one large study, single runs more than 10% longer than the previous 30 days' longest were associated with more overuse injuries, so a few smaller steps stay under that line.`, chart: 'longruns', evidence: EVIDENCE.obs });
    }
    return out;
  }

  // merge demo: coverage numbers for the two-source persona (prototype values)
  function coverage(gen) {
    const pair = gen.runs.filter((r) => r.inStrava && r.inGarmin);
    const review = pair.filter((r) => r.id % 47 === 0);
    return {
      total: gen.runs.length, both: pair.length, sure: pair.length - review.length, review,
      stravaOnly: gen.runs.filter((r) => r.inStrava && !r.inGarmin).length,
      garminOnly: gen.runs.filter((r) => !r.inStrava && r.inGarmin).length,
      gainedHr: pair.filter((r) => r.id % 9 === 0).length, gainedCad: pair.filter((r) => r.id % 13 === 0).length,
    };
  }

  const api = { PERSONAS, generate, report, coverage, raceHistory, fmtPace, fmtDuration, fmtHours, monthName, shortDate, plural, MONTHS, EVIDENCE, raceTime, nominalOf, dayKey, fromKey, addDays, daysBetween };
export default api;
